"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  VStack,
  HStack,
  Spinner,
  Center,
  Link as ChakraLink,
  Alert,
  AlertIcon,
  Image,
  Icon,
} from "@chakra-ui/react";
import { FiFileText, FiFile, FiCreditCard, FiLogOut } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { FiUser } from "react-icons/fi";
import { supabase } from "../supabaseClient";
import type { Document, Profile } from "../types/types";

export default function Dashboard() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const totalRemainingCredits =
    (profile?.subscription_remaining_credits || 0) +
    (profile?.purchased_remaining_credits || 0);

  useEffect(() => {
    async function getProfileAndDocuments() {
      try {
        setLoading(true);
        setError(null);

        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          throw new Error("User not found");
        }

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (profileError) {
          console.error("Profile fetch error:", profileError);
          throw profileError;
        }

        if (!profileData) {
          throw new Error("Profile not found");
        }

        setProfile(profileData);

        const { data: documentsData, error: documentsError } = await supabase
          .from("documents")
          .select(
            `
            *,
            document_type:document_type_id(name, description)
          `
          )
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(5);

        if (documentsError) {
          throw documentsError;
        }

        setRecentDocuments(documentsData || []);
      } catch (error: any) {
        console.error("Error loading dashboard data:", error.message);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    getProfileAndDocuments();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PROCESSING":
        return "yellow";
      case "COMPLETED":
        return "green";
      case "FAILED":
        return "red";
      default:
        return "gray";
    }
  };

  if (loading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" color="primary.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box
        bg="rgb(253, 251, 252)"
        shadow="sm"
        position="sticky"
        top={0}
        zIndex={10}
      >
        <Container maxW="7xl">
          <Flex h={16} align="center" justify="space-between">
            <HStack spacing={4}>
              <Image
                src="/main-logo.png"
                alt="RadiusDocs AI"
                height="50px"
                width="auto"
                objectFit="contain"
              />
            </HStack>

            <HStack spacing={6}>
              <ChakraLink
                as={Link}
                to="/dashboard"
                color="primary.600"
                fontWeight="medium"
              >
                Dashboard
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/document-history"
                color="gray.600"
                fontWeight="medium"
              >
                Documents
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/templates"
                color="gray.600"
                fontWeight="medium"
              >
                Templates
              </ChakraLink>
              <ChakraLink
                as={Link}
                to="/billing"
                color="gray.600"
                fontWeight="medium"
              >
                Billing
              </ChakraLink>
            </HStack>

            <HStack spacing={4}>
              <ChakraLink
                as={Link}
                to="/account"
                color="gray.700"
                fontWeight="medium"
              >
                {profile?.full_name || profile?.email}
              </ChakraLink>
              <Button
                onClick={handleSignOut}
                variant="ghost"
                size="sm"
                leftIcon={<Icon as={FiLogOut} />}
                colorScheme="red"
              >
                Sign Out
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container maxW="7xl" py={8}>
        {error && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        {/* Welcome Section */}
        <Flex justify="space-between" align="center" mb={8}>
          <VStack align="start" spacing={1}>
            <Heading size="xl" color="gray.800">
              Welcome, {profile?.full_name || profile?.email?.split("@")[0]}
            </Heading>
            <Text color="gray.600">
              Generate AI-powered medical documents with ease.
            </Text>
          </VStack>
          <Button
            as={Link}
            to="/submit-document"
            colorScheme="primary"
            size="lg"
            leftIcon={<Icon as={FiFileText} />}
          >
            New Document
          </Button>
        </Flex>

        {/* Stats */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Available Credits</StatLabel>
                <StatNumber color="primary.600">
                  {totalRemainingCredits}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Recent Documents</StatLabel>
                <StatNumber color="primary.600">
                  {recentDocuments.length}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Recent Documents */}
        <Box mb={8}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="md" color="gray.800">
              Recent Documents
            </Heading>
            <ChakraLink
              as={Link}
              to="/document-history"
              color="primary.600"
              fontSize="sm"
            >
              View All
            </ChakraLink>
          </Flex>

          {recentDocuments.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              {recentDocuments.map((doc) => (
                <Card
                  key={doc.id}
                  as={Link}
                  to={`/document/${doc.id}`}
                  _hover={{ transform: "translateY(-2px)", shadow: "md" }}
                  transition="all 0.2s"
                  cursor="pointer"
                >
                  <CardBody>
                    <VStack align="start" spacing={3}>
                      <Badge colorScheme={getStatusColor(doc.status)} size="sm">
                        {doc.status.replace("_", " ")}
                      </Badge>
                      <Heading size="sm" color="gray.800">
                        {doc.document_type?.name === "Miscellaneous" &&
                        doc.custom_document_name
                          ? doc.custom_document_name
                          : doc.document_type?.name || "Document"}
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </Text>
                      <Text fontSize="sm" color="gray.600" noOfLines={3}>
                        {doc.prompt.length > 100
                          ? `${doc.prompt.substring(0, 100)}...`
                          : doc.prompt}
                      </Text>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <Card>
              <CardBody textAlign="center" py={12}>
                <VStack spacing={4}>
                  <Text color="gray.600">
                    You haven't created any documents yet.
                  </Text>
                  <Button
                    as={Link}
                    to="/submit-document"
                    colorScheme="primary"
                    leftIcon={<Icon as={FiFileText} />}
                  >
                    Create your first document
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>

        {/* Quick Actions */}
        <Box>
          <Heading size="md" color="gray.800" mb={4}>
            Quick Actions
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <Card
              as={Link}
              to="/submit-document"
              _hover={{ transform: "translateY(-2px)", shadow: "md" }}
              transition="all 0.2s"
              cursor="pointer"
            >
              <CardBody>
                <HStack spacing={4}>
                  <Box
                    p={3}
                    bg="primary.50"
                    borderRadius="lg"
                    color="primary.600"
                  >
                    <Icon as={FiFileText} boxSize={6} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" color="gray.800">
                      New Document
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Create a new AI-generated document
                    </Text>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>

            <Card
              as={Link}
              to="/templates"
              _hover={{ transform: "translateY(-2px)", shadow: "md" }}
              transition="all 0.2s"
              cursor="pointer"
            >
              <CardBody>
                <HStack spacing={4}>
                  <Box p={3} bg="green.50" borderRadius="lg" color="green.600">
                    <Icon as={FiFile} boxSize={6} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" color="gray.800">
                      Manage Templates
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Upload and manage your document templates
                    </Text>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>

            <Card
              as={Link}
              to="/billing"
              _hover={{ transform: "translateY(-2px)", shadow: "md" }}
              transition="all 0.2s"
              cursor="pointer"
            >
              <CardBody>
                <HStack spacing={4}>
                  <Box
                    p={3}
                    bg="orange.50"
                    borderRadius="lg"
                    color="orange.600"
                  >
                    <Icon as={FiCreditCard} boxSize={6} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" color="gray.800">
                      Billing History
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      View your billing history and invoices
                    </Text>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
            <Card
              as={Link}
              to="/account"
              _hover={{ transform: "translateY(-2px)", shadow: "md" }}
              transition="all 0.2s"
              cursor="pointer"
            >
              <CardBody>
                <HStack spacing={4}>
                  <Box
                    p={3}
                    bg="purple.50"
                    borderRadius="lg"
                    color="purple.600"
                  >
                    <Icon as={FiUser} boxSize={6} />
                  </Box>
                  <VStack align="start" spacing={1}>
                    <Heading size="sm" color="gray.800">
                      Account Settings
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Update your profile and preferences
                    </Text>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        </Box>
      </Container>
    </Box>
  );
}
